import { Routes, Route, useLocation } from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import Layout from "./layout/Layout";
import Tenants from "./pages/Tenants/index";
import LoginPage from "./pages/login";
import ProtectedRoute from "./components/ProtectedRoute";
import { useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import WrongPortalPage from "./pages/wrongPortal";
import TenantDetails from "./pages/TenantDetails";
import ProductAssignment from "./pages/ProductAssignment/steps/productSelection";
import NotFound from "../../../packages/ui/src/pages/notFound";
import ProductDetails from "./pages/ProductDetails";
import PurchaseOrderDetailsView from "./pages/PurchaseOrderDetailsView";
import PurchaseOrders from "./pages/PurchaseOrders";
import Catalog from "./pages/Catalog";
import {
  ADMIN_PUBLIC_ROUTES,
  ADMIN_PROTECTED_ROUTES,
  isPublicRoute,
  isNotFoundRoute,
} from "./routes";
import Users from "./pages/Users";
import UserDetails from "./pages/UserDetails";
import { toast } from "sonner";
import { useDispatch } from "react-redux";
import { setGlobalState, logout } from "./store/auth";
import { store } from "./store";
import { useQueryClient } from "@tanstack/react-query";

function App() {
  const location = useLocation();
  const auth = useAuth();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const pendingAuthProcessedRef = useRef(false);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnlineStatusChange = () => {
      if (navigator.onLine) {
        toast.success("You are back online!", {
          duration: 3000,
        });
      } else {
        toast.error("You are offline. Please check your internet connection.", {
          duration: 5000,
        });
      }
    };

    // Listen for online/offline events
    window.addEventListener("online", handleOnlineStatusChange);
    window.addEventListener("offline", handleOnlineStatusChange);

    // Cleanup event listeners on unmount
    return () => {
      window.removeEventListener("online", handleOnlineStatusChange);
      window.removeEventListener("offline", handleOnlineStatusChange);
    };
  }, []);

  useEffect(() => {
    // Listen for logout events from API client
    const handleAuthLogout = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log("🚨 [APP] 401 LOGOUT EVENT DETECTED!", customEvent.detail);
      console.log("🚨 [APP] Clearing all storage and logging out...");

      // CRITICAL: Cancel all pending React Query requests
      // This prevents retries from happening after we clear the token
      console.log("🛑 [APP] Cancelling all pending queries");
      queryClient.cancelQueries();
      queryClient.clear();

      localStorage.clear();
      sessionStorage.clear();
      
      // Clear Redux state
      store.dispatch(logout());
      auth?.removeUser();
      
      // Perform Keycloak logout
      auth.signoutRedirect();
    };

    // Add event listener - this automatically filters for "auth:logout" events
    window.addEventListener("auth:logout", handleAuthLogout);

    // Cleanup on unmount
    return () => {
      window.removeEventListener("auth:logout", handleAuthLogout);
    };
  }, [auth]);

  // Note: Token setting is now handled in main.tsx onSigninCallback and authenticateUser
  // Removed redundant useEffect that was causing race conditions

  // Handle pending auth data from OIDC callback
  useEffect(() => {
    // Prevent multiple executions using ref
    if (pendingAuthProcessedRef.current) {
      return;
    }

    const pendingAuthData = sessionStorage.getItem("pendingAuthData");

    if (pendingAuthData) {
      try {
        const authData = JSON.parse(pendingAuthData);
        
        // CRITICAL: Validate that pendingAuthData has actual user data
        // Don't process if it's empty/undefined - prevents clearing existing auth state
        if (!authData.userInfo?.subject_id) {
          console.error("❌ [APP] pendingAuthData has no subject_id - SKIPPING to prevent clearing auth state");
          sessionStorage.removeItem("pendingAuthData");
          pendingAuthProcessedRef.current = true;
          return;
        }
        
        if (authData.userInfo) {
          const userInfo = authData.userInfo;

          dispatch(
            setGlobalState({
              first_name: userInfo.first_name || null,
              last_name: userInfo.last_name || null,
              full_name: userInfo.full_name || null,
              email: userInfo.email || null,
              subject_id: userInfo.subject_id || null,
              creation_date: userInfo.creation_date || null,
              user_permissions: userInfo.user_permissions || [],
              user_branches: userInfo.user_branches
                ? userInfo.user_branches
                    .map((branch: { id: number; name: string }) => branch.id)
                    .join(",")
                : "",
              // Token data from auth payload
              access_token: authData.access_token || "",
              id_token: authData.id_token || "",
              refresh_token: authData.refresh_token || "",
              expires_at: authData.expires_at || 0,
            })
          );
          sessionStorage.removeItem("pendingAuthData");
          // Mark as processed
          pendingAuthProcessedRef.current = true;
        }
      } catch (error) {
        console.error("Error parsing pending auth data:", error);
      }
    }
  }, [dispatch]);

  // NEW: Add broadcast channel listener for cross-tab auth sync
  useEffect(() => {
    const authChannel = new BroadcastChannel("auth-sync");

    // Listen for auth events from other tabs
    authChannel.onmessage = (event) => {
      const { type, data } = event.data;

      switch (type) {
        case "LOGOUT":
          dispatch(logout());
          auth.removeUser();
          // Don't redirect here, let the component handle it
          break;
        case "LOGIN":
          dispatch(setGlobalState(data));
          break;
        case "TOKEN_REFRESH":
          // Update tokens across tabs
          localStorage.setItem("token", data.access_token);
          break;
      }
    };

    // Cleanup
    return () => {
      authChannel.close();
    };
  }, [dispatch, auth]);

  // If current path is a public route - render without layout
  if (isPublicRoute(location.pathname) || isNotFoundRoute(location.pathname)) {
    return (
      <Routes>
        <Route path={ADMIN_PUBLIC_ROUTES.LOGIN} element={<LoginPage />} />
        <Route
          path={ADMIN_PUBLIC_ROUTES.WRONG_PORTAL}
          element={<WrongPortalPage />}
        />
        <Route path={ADMIN_PUBLIC_ROUTES.NOT_FOUND} element={<NotFound />} />
      </Routes>
    );
  }

  // Protected routes that need authentication - render with layout
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route
          path={ADMIN_PROTECTED_ROUTES.HOME}
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path={ADMIN_PROTECTED_ROUTES.CATALOG}
          element={
            <ProtectedRoute>
              <Catalog />
            </ProtectedRoute>
          }
        />
        <Route
          path={ADMIN_PROTECTED_ROUTES.USERS}
          element={
            <ProtectedRoute>
              <Users />
            </ProtectedRoute>
          }
        />
        <Route
          path={ADMIN_PROTECTED_ROUTES.DASHBOARD}
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path={ADMIN_PROTECTED_ROUTES.TENANTS}
          element={
            <ProtectedRoute>
              <Tenants />
            </ProtectedRoute>
          }
        />
        <Route
          path={ADMIN_PROTECTED_ROUTES.PURCHASE_ORDERS}
          element={
            <ProtectedRoute>
              <PurchaseOrders />
            </ProtectedRoute>
          }
        />
        <Route
          path={ADMIN_PROTECTED_ROUTES.TENANT_DETAILS}
          element={
            <ProtectedRoute>
              <TenantDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path={ADMIN_PROTECTED_ROUTES.TENANT_DETAILS_PURCHASE_ORDER_DETAILS}
          element={
            <ProtectedRoute>
              <PurchaseOrderDetailsView />
            </ProtectedRoute>
          }
        />
        <Route
          path={ADMIN_PROTECTED_ROUTES.TENANT_DETAILS_USER_DETAILS}
          element={
            <ProtectedRoute>
              <UserDetails />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route
        path={ADMIN_PROTECTED_ROUTES.PRODUCT_ASSIGNMENT}
        element={
          <ProtectedRoute>
            <ProductAssignment />
          </ProtectedRoute>
        }
      />
      <Route
        path={ADMIN_PROTECTED_ROUTES.PRODUCT_DETAILS}
        element={
          <ProtectedRoute>
            <ProductDetails />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default App;
